/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import {
    classNamesFunction,
    DefaultButton,
    Icon,
    IconButton,
    IMessageBarStyleProps,
    IMessageBarStyles,
    Link,
    MessageBar,
    MessageBarType
} from '@fluentui/react';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logSelection } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { useModelGuidanceCardsData } from 'src/customText/core/hooks/useModelGuidanceCardsData';
import { ModelGuidanceCardMessages } from 'src/modules/modelGuidance/components/modelGuidanceCardMessages';
import styled from 'styled-components';

type CustomTextModelGuidanceRecommendationsProps = {
    cardKeyToNavigate: string;
    selectedModelName: string;
    isExtractionProject: boolean;
};

const messages = defineMessages({
    modelDropDownLabel: {
        id: 'customText.customTextModelGuidanceRecommendations.modelDropDownLabel',
        defaultMessage: 'Recommendations from model'
    },
    modelDropDownPlaceHolder: {
        id: 'customText.customTextModelGuidanceRecommendations.modelDropDownPlaceHolder',
        defaultMessage: 'No models available.'
    },
    guidanceTitle: {
        id: 'customText.customTextModelGuidanceRecommendations.guidanceTitle',
        defaultMessage: 'Retrain the model in order to see updates to recommendations'
    },
    collapse: {
        id: 'customText.customTextModelGuidanceRecommendations.collapse',
        defaultMessage: 'Collapse recommendation'
    },
    expand: {
        id: 'customText.customTextModelGuidanceRecommendations.expand',
        defaultMessage: 'Expand recommendation'
    },
    modelPerformanceMessage: {
        id: 'customText.customTextModelGuidanceRecommendations.modelPerformanceMessage',
        defaultMessage: 'Go back to Model performance to resume viewing model details'
    },
    modelPerformanceButton: {
        id: 'customText.customTextModelGuidanceRecommendations.modelPerformanceButton',
        defaultMessage: 'Return to model performance'
    }
});

const theme = getScopedTheme('ModelGuidanceCard');

const ModelPerformanceMessageBarStyles = classNamesFunction<IMessageBarStyleProps, IMessageBarStyles>()({
    root: {
        width: '100%'
    },
    innerText: {
        width: '100%'
    }
});

const RecommendationTab = styled(Stack)({
    minHeight: 0,
    padding: '0px 5px 30px'
});

const GuidanceCard = styled(Stack)({
    padding: '15px',
    borderBottom: `1px solid ${theme.borderColor}`
});

const CardTitle = styled(LuisText)<{ numberOfIssues: number }>(props => ({
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: props.numberOfIssues === 0 ? 400 : 600
}));

const GuidanceTitle = styled(LuisText)({
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 400,
    margin: '10px 0 !important'
});

const Title = styled(LuisText)({
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px'
});

const GuidanceCardIcon = styled(Icon)<{ color: string }>(props => ({
    marginRight: '19px',
    color: props.color
}));

const arrowStyles = {
    icon: {
        color: theme.arrowColor
    }
};

export const CustomTextModelGuidanceRecommendations = observer((props: CustomTextModelGuidanceRecommendationsProps) => {
    const { isExtractionProject, selectedModelName, cardKeyToNavigate } = props;

    const history = useHistory();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const currentProject = rootStore.currentCustomTextProjectStore;
    const [selectedGuidances, setSelectedGuidances] = React.useState<string[]>([]);
    const [isModelPerformanceMessageHidden, setIsModelPerformanceMessageHidden] = React.useState(!cardKeyToNavigate);
    const [selectedModel, setSelectedModel] = React.useState(selectedModelName ?? currentProject.trainedModels?.items[0]?.name ?? null);

    React.useEffect(() => {
        setSelectedGuidances([]);
    }, [selectedModel]);

    const modelOptions = React.useMemo(
        (): IDropdownOption[] =>
            currentProject.trainedModels.items.map(model => ({
                key: model.name,
                text: model.name
            })),
        [currentProject.trainedModels.items]
    );

    const modelGuidanceStore = React.useMemo(() => currentProject.modelsGuidanceMap?.get(selectedModel), [
        selectedModel,
        currentProject,
        currentProject.modelsGuidanceMap?.size
    ]);

    const { modelGuidanceCardArray } = useModelGuidanceCardsData({ modelGuidanceStore, isExtractionProject });

    const routeToTraining = () => history.push(routeTable.customText.trainingPage(currentProject.id, currentProject.projectKind));

    const handleOnCardClick = (cardKey: string) => {
        let newGuidances: string[] = Array.from(selectedGuidances);

        if (newGuidances.includes(cardKey)) {
            newGuidances.splice(newGuidances.indexOf(cardKey), 1);
        } else {
            newGuidances = newGuidances.concat([cardKey]);
        }

        setSelectedGuidances(newGuidances);
    };

    const returnToModelPerformancePage = () => {
        history.push(routeTable.customText.modelDetailsPage(currentProject.id, currentProject.projectKind, selectedModel));
    };

    const getModelPerformanceInfoMessage = () => (
        <MessageBar
            isMultiline
            dismissButtonAriaLabel="Close"
            messageBarType={MessageBarType.info}
            styles={ModelPerformanceMessageBarStyles}
            onDismiss={() => setIsModelPerformanceMessageHidden(true)}>
            <Stack styles={{ root: { width: '100%' } }} tokens={{ childrenGap: '10px' }}>
                <LuisText>{localize(messages.modelPerformanceMessage)}</LuisText>
                <DefaultButton
                    onClick={returnToModelPerformancePage}
                    text={localize(messages.modelPerformanceButton)}
                    ariaLabel={localize(messages.modelPerformanceButton)}
                />
            </Stack>
        </MessageBar>
    );

    const GuidanceCardInfo = (CardProps: { cardKey: string; numberOfIssues: number; rightMessage: string; errorMessage: string }) => {
        const { cardKey, numberOfIssues, rightMessage, errorMessage } = CardProps;
        const iconColor =
            numberOfIssues === 0 ? theme.checkMarkIconColor : numberOfIssues < 3 ? theme.warningIconColor : theme.errorIconColor;

        return (
            <GuidanceCard horizontal verticalAlign="center" tokens={{ childrenGap: '10px' }}>
                {numberOfIssues > 0 && (
                    <IconButton
                        role="button"
                        styles={arrowStyles}
                        onClick={() => handleOnCardClick(cardKey)}
                        data-automation-id={selectedGuidances.includes(cardKey) ? 'ChevronDown' : 'Forward'}
                        iconProps={{ iconName: selectedGuidances.includes(cardKey) ? 'ChevronDown' : 'Forward' }}
                        ariaLabel={localize(selectedGuidances.includes(cardKey) ? messages.collapse : messages.expand)}
                    />
                )}
                <GuidanceCardIcon
                    color={iconColor}
                    iconName={numberOfIssues === 0 ? 'CheckMark' : numberOfIssues < 3 ? 'warning' : 'infoSolid'}
                />
                <CardTitle numberOfIssues={numberOfIssues}>
                    {numberOfIssues === 0 ? rightMessage : errorMessage}
                    {numberOfIssues > 0 && ` (${numberOfIssues})`}
                </CardTitle>
            </GuidanceCard>
        );
    };

    const RecommendationInfo = (cardProps: { title: string; description: string; labels: string[] }) => {
        const { title, description, labels } = cardProps;

        return (
            <Stack styles={{ root: { padding: '20px' } }} tokens={{ childrenGap: '20px' }}>
                <Stack>
                    <Title>{localize(ModelGuidanceCardMessages.recommendation)}</Title>

                    <LuisText>{description}</LuisText>
                </Stack>

                <Stack>
                    <Title>{title}</Title>

                    {labels.map(label => (
                        <LuisText key={label}>{label}</LuisText>
                    ))}
                </Stack>
            </Stack>
        );
    };

    const getModelGuidanceContent = (
        <Stack>
            {modelGuidanceCardArray
                .sort((a, b) => (a.numberOfIssues > b.numberOfIssues ? -1 : 1))
                .map(guidance => (
                    <Stack>
                        <GuidanceCardInfo
                            cardKey={guidance.cardKey}
                            errorMessage={guidance.shortErrorMessage}
                            rightMessage={guidance.shortRightMessage}
                            numberOfIssues={guidance.numberOfIssues}
                        />
                        {selectedGuidances.includes(guidance.cardKey) && guidance.labels.length > 0 && (
                            <RecommendationInfo
                                labels={guidance.labels}
                                title={guidance.recommendationTitle}
                                description={guidance.recommendationDescription}
                            />
                        )}
                    </Stack>
                ))}
        </Stack>
    );

    return modelGuidanceStore ? (
        <RecommendationTab>
            {!isModelPerformanceMessageHidden && <Stack.Item>{getModelPerformanceInfoMessage()}</Stack.Item>}

            <LuisDropdown
                options={modelOptions}
                selectedKey={selectedModel}
                label={localize(messages.modelDropDownLabel)}
                ariaLabel={localize(messages.modelDropDownLabel)}
                onChange={(_, item) => {
                    setSelectedModel(item.text);
                    logSelection(serviceName, 'DataLabeling', 'RecommendationModel', 'Recommendations', currentProject.id);
                }}
                placeholder={localize(messages.modelDropDownPlaceHolder)}
            />

            <GuidanceTitle>{localize(messages.guidanceTitle)}</GuidanceTitle>

            {getModelGuidanceContent}
        </RecommendationTab>
    ) : (
        <EmptyStateView
            message={
                <>
                    {localize(ModelGuidanceCardMessages.noRecommendationAvailable1)}
                    &nbsp;
                    <RBACTooltip
                        hidden={canWrite}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="trainModelLinkLanguageToolTip"
                        componentSurroundedByToolTip={
                            <Link disabled={!canWrite} onClick={routeToTraining} styles={{ root: { color: theme.emptyStateLinkColor } }}>
                                {localize(ModelGuidanceCardMessages.retrain)}
                            </Link>
                        }
                    />
                    &nbsp;
                    {localize(ModelGuidanceCardMessages.noRecommendationAvailable2)}
                </>
            }
        />
    );
});
